// =============================================================================
// supplier-client.http.ts — Adaptateur HTTP RÉEL du SupplierClient (référentiel fournisseur, A-03/04/05).
// =============================================================================
// Remplace le stub-first derrière le MÊME DI seam (SupplierClientProvider) : les hooks/écrans
// consomment l'interface SupplierClient, l'impl est substituée sans toucher l'UI. Miroir des routes
// ADMIN de D5 (supplier-reference-admin.controller.ts, @Controller('admin/establishments')).
//
// AUTORITÉ = API (NestJS, AdminSessionGuard, withTenant(target_siret), transitions d'état,
// enrôlement 2 actes, révocation/transfert MASTER, refresh enrichissement fail-open). Cet adaptateur
// ne porte AUCUNE règle métier : il sérialise l'intention, parse la réponse, relaie le message
// d'erreur serveur. INVARIANT n°1 : le SIRET part TOUJOURS dans l'URL (server-set), JAMAIS du body.
//
// SÉCURITÉ (RULE_REF_006) : la session admin est portée par le COOKIE httpOnly posé par l'API
// (`credentials: 'include'`). CSRF DOUBLE-SUBMIT : les mutations (POST) ré-émettent le cookie
// NON-httpOnly `admin_csrf` en en-tête `x-csrf-token` (AdminSessionGuard l'exige ; sans lui = 403).
// Les GET (list/get) ne portent PAS de CSRF. Aucune donnée métier en localStorage. Le secret de
// session httpOnly n'est JAMAIS lu par le SPA.

import {
  SupplierApiError,
  type AddContactInput,
  type AddContactResult,
  type AdminEstablishmentDto,
  type AdminListPageDto,
  type AdminListQueryDto,
  type ContactTokenAccessDto,
  type ContactTokenStatusDto,
  type EnrichmentRefreshResult,
  type EnrollInput,
  type EnrollResult,
  type InvitationInput,
  type RevokeContactInput,
  type SupplierClient,
  type TransferMasterInput,
} from '@/api/supplier-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException `{ statusCode, message }`). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/**
 * Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly, lisible par le SPA même origine).
 * `undefined` si absent (pas de session, ou contexte sans document) — l'appel mutant partira
 * alors sans en-tête et l'API répondra 403 (fail-closed côté serveur, jamais d'écriture).
 * Parité stricte avec HttpCampaignClient (campaign-client.http.ts).
 */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel : `fetch` contre l'API admin supplier-reference. `baseUrl` par défaut vide
 * (chemins relatifs : SPA et API partagent l'origine, ou un reverse proxy route `/admin/*`). En
 * test, on injecte un baseUrl ou on mocke `fetch`.
 */
export class HttpSupplierClient implements SupplierClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  /** Construit l'URL absolue (baseUrl + chemin relatif racine API). */
  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  /**
   * Exécute une requête et renvoie le JSON typé (ou `undefined` si `expectBody=false`). Sur les
   * méthodes MUTANTES (POST), pose l'en-tête CSRF `x-csrf-token` = cookie `admin_csrf`. En cas
   * d'erreur HTTP, lève une SupplierApiError (message serveur relayé tel quel).
   */
  private async request<T>(
    path: string,
    init: RequestInit,
    expectBody: boolean,
  ): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (method !== 'GET' && method !== 'HEAD') {
      const csrf = readCsrfToken();
      if (csrf !== undefined) {
        headers['x-csrf-token'] = csrf;
      }
    }

    const response = await fetch(this.url(path), {
      // Session admin = cookie httpOnly : transmis avec credentials: 'include'.
      credentials: 'include',
      ...init,
      headers: { ...headers, ...init.headers },
    });

    if (!response.ok) {
      throw await this.toApiError(response);
    }
    if (!expectBody) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse non-OK en SupplierApiError typée (message serveur tel quel). */
  private async toApiError(response: Response): Promise<SupplierApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur le fournisseur.`;
    // Le statut HTTP est propagé (machine-code) : le renvoi d'activation discrimine 429/409/404
    // PAR CODE, sans dépendre du message serveur brut.
    return new SupplierApiError(message, response.status);
  }

  private get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' }, true);
  }

  /** POST admin (corps JSON) ; `expectBody=false` pour les transitions sans corps de réponse. */
  private post<T>(path: string, payload: unknown, expectBody: boolean): Promise<T> {
    return this.request<T>(
      path,
      { method: 'POST', body: JSON.stringify(payload) },
      expectBody,
    );
  }

  /** Chemin d'un établissement (SIRET encodé, TOUJOURS depuis l'URL — invariant n°1). */
  private establishmentPath(siret: string, suffix = ''): string {
    return `/admin/establishments/${encodeURIComponent(siret)}${suffix}`;
  }

  // --- Lectures (A-03 / A-04) ------------------------------------------------

  public listEstablishments(query: AdminListQueryDto = {}): Promise<AdminListPageDto> {
    const qs = HttpSupplierClient.serializeListQuery(query);
    return this.get<AdminListPageDto>(
      `/admin/establishments${qs === '' ? '' : `?${qs}`}`,
    );
  }

  /**
   * Sérialise les critères du registre en query-string (`URLSearchParams`). `state` et `sirets` sont
   * RÉPÉTÉS (une entrée par valeur, parité `@Query()` NestJS répété/CSV). Les défauts absents ne sont
   * pas écrits (URL propre) ; les valeurs sont posées TELLES QUELLES — la validation (allowlist/bornes)
   * est l'autorité de l'API (fail-closed 400).
   */
  private static serializeListQuery(query: AdminListQueryDto): string {
    const params = new URLSearchParams();
    if (query.q !== undefined && query.q !== '') {
      params.set('q', query.q);
    }
    for (const state of query.state ?? []) {
      params.append('state', state);
    }
    if (query.from !== undefined && query.from !== '') {
      params.set('from', query.from);
    }
    if (query.to !== undefined && query.to !== '') {
      params.set('to', query.to);
    }
    for (const siret of query.sirets ?? []) {
      params.append('sirets', siret);
    }
    // `tokenStatus` RÉPÉTÉ (parité `state`) : filtre statut d'activation, composition résolue SERVEUR.
    for (const tokenStatus of query.tokenStatus ?? []) {
      params.append('tokenStatus', tokenStatus);
    }
    if (query.sort !== undefined) {
      params.set('sort', query.sort);
    }
    if (query.dir !== undefined) {
      params.set('dir', query.dir);
    }
    if (query.page !== undefined) {
      params.set('page', String(query.page));
    }
    if (query.pageSize !== undefined) {
      params.set('pageSize', String(query.pageSize));
    }
    return params.toString();
  }

  public getEstablishment(siret: string): Promise<AdminEstablishmentDto> {
    return this.get<AdminEstablishmentDto>(this.establishmentPath(siret));
  }

  public listContactsTokenStatus(
    siret: string,
  ): Promise<readonly ContactTokenStatusDto[]> {
    return this.get<readonly ContactTokenStatusDto[]>(
      this.establishmentPath(siret, '/contacts/token-status'),
    );
  }

  // T6-TOKENS — vue enrichie « Accès & tokens » (GET, read-only, sans CSRF). Endpoint DISTINCT de
  // token-status : élargit la projection (dates + compteur/plafond) sans toucher le contrat T2-REGISTRE.
  public listContactsTokenAccess(
    siret: string,
  ): Promise<readonly ContactTokenAccessDto[]> {
    return this.get<readonly ContactTokenAccessDto[]>(
      this.establishmentPath(siret, '/contacts/token-access'),
    );
  }

  public async resendContactActivation(
    siret: string,
    contactId: string,
  ): Promise<void> {
    await this.post<void>(
      this.establishmentPath(
        siret,
        `/contacts/${encodeURIComponent(contactId)}/resend-activation`,
      ),
      {},
      false,
    );
  }

  // --- A-05 : enrôlement en 2 ACTES DISTINCTS --------------------------------

  public enroll(siret: string, input: EnrollInput): Promise<EnrollResult> {
    return this.post<EnrollResult>(this.establishmentPath(siret, '/enroll'), input, true);
  }

  public async sendInvitation(
    siret: string,
    input: InvitationInput,
  ): Promise<void> {
    await this.post<void>(this.establishmentPath(siret, '/invitation'), input, false);
  }

  // --- Transitions d'état (manuel ADMIN) — cible = SIRET d'URL (invariant n°1) ---
  //
  // T3-SUSPENSION : suspend/refuse portent le MOTIF (`{ reason }`) dans le corps (texte libre,
  // non-scoping — parité `revoke`). reactivate/archive restent à corps vide (schémas `.strict()`,
  // aucun reason). `adminId`/IP sont dérivés SERVEUR de la session admin (jamais envoyés par le front).

  public async refuse(siret: string, reason: string): Promise<void> {
    await this.post<void>(this.establishmentPath(siret, '/refuse'), { reason }, false);
  }

  public async suspend(siret: string, reason: string): Promise<void> {
    await this.post<void>(this.establishmentPath(siret, '/suspend'), { reason }, false);
  }

  public async reactivate(siret: string): Promise<void> {
    await this.post<void>(this.establishmentPath(siret, '/reactivate'), {}, false);
  }

  public async archive(siret: string): Promise<void> {
    await this.post<void>(this.establishmentPath(siret, '/archive'), {}, false);
  }

  // --- Contacts (admin) ------------------------------------------------------

  // BUG2-ADD-CONTACT (DEC-18) — POST :siret/contacts : ajout d'un contact admin (sans session
  // MASTER). Le SIRET part dans l'URL (invariant n°1) ; le body porte {email,nom,prenom,domainOverride?}.
  // Renvoie {newContactId}. L'erreur domaine (422 hors-domaine sans dérogation) remonte telle quelle.
  public addContact(
    siret: string,
    input: AddContactInput,
  ): Promise<AddContactResult> {
    return this.post<AddContactResult>(
      this.establishmentPath(siret, '/contacts'),
      input,
      true,
    );
  }

  public async revokeContact(
    siret: string,
    input: RevokeContactInput,
  ): Promise<void> {
    await this.post<void>(
      this.establishmentPath(siret, '/contacts/revoke'),
      input,
      false,
    );
  }

  public async transferMaster(
    siret: string,
    input: TransferMasterInput,
  ): Promise<void> {
    await this.post<void>(
      this.establishmentPath(siret, '/contacts/transfer-master'),
      input,
      false,
    );
  }

  // --- Enrichissement (fail-open : dégradé → saisie manuelle A-04) ------------

  /**
   * Parse la réponse en UNION DISCRIMINÉE `EnrichmentRefreshResult` (miroir du Published Language
   * `EnrichmentResult`) : branche `ok` (snapshot frais) OU `degraded` (`reason` + `stale?` — dernier
   * snapshot périmé s'il est connu). Aucune réinterprétation : la forme sérialisée par l'API porte
   * déjà les deux branches (cast typé, cohérent avec les autres lectures de cet adaptateur).
   */
  public refreshEnrichment(siret: string): Promise<EnrichmentRefreshResult> {
    return this.post<EnrichmentRefreshResult>(
      this.establishmentPath(siret, '/enrichment/refresh'),
      {},
      true,
    );
  }
}
